import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Stack,
  IconButton,
  Typography,
  Alert,
  Checkbox,
  Box,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { useRequestEnterpriseInfo } from '@features/payments/hooks/useRequestEnterpriseInfo';

import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';

interface FormValues {
  name: string;
  locations: number;
  contactPreference: string[];
  phoneNumber: string;
  email: string;
}

const ReqMoreInfoModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  handleCloseParent?: () => void;
  onSubmit?: (data: FormValues) => void;
}> = ({ open, handleClose, handleCloseParent }) => {
  const [errorText, setErrorText] = React.useState<string>('');
  const { requestEnterpriseInfo, isRequestEnterpriseInfoLoading } =
    useRequestEnterpriseInfo({ setErrorText });
  const { updateProfile } = useUpdateProfile({});
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const getStrippedText = (html: string): string => {
    return html
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, '')
      .trim();
  };

  const isValidUSPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    return /^1?\d{10}$/.test(cleaned);
  };

  const handleFormSubmit = (data: FormValues) => {
    track(amplitudeEventsName.FORM_SUBMITTED_MULTIPLE_LOCATIONS)
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          handleCloseParent?.();
          updateProfile({ has_more_locations_answered: true });
          handleClose();
          toast.success('A representative will reach out shortly!');
        }
      },
    };
    requestEnterpriseInfo(data, options);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle textAlign="center">
        <IconButton
          sx={{ position: 'absolute', top: 10, right: 10 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <SupervisorAccountIcon
          sx={{
            fontSize: 60,
            color: 'primary.main',
            margin: 'auto !important',
          }}
        />
        <Typography variant="h4" sx={{ pt: 2, fontWeight: 'bold' }}>
          Multi-Unit Information
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            rules={{
              required: 'Your name is required',
              validate: (value) =>
                getStrippedText(value).length > 0 || 'Name cannot be empty',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                fullWidth
                margin="normal"
                size="small"
                error={!!errors.name}
                helperText={errors.name ? errors.name.message : ''}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: 'Email is required',
              validate: (value) =>
                getStrippedText(value).length > 0 || 'Name cannot be empty',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                message: 'Invalid email address',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                fullWidth
                size="small"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
              />
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            defaultValue=""
            rules={{
              required: 'Phone number is required',
              validate: (value) =>
                isValidUSPhoneNumber(value) || 'Enter a valid US phone number',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Phone number"
                fullWidth
                margin="normal"
                error={!!errors.phoneNumber}
                helperText={
                  errors.phoneNumber ? errors.phoneNumber.message : ''
                }
              />
            )}
          />
          <Controller
            name="locations"
            control={control}
            defaultValue={1}
            rules={{
              required: 'Number of locations is required',
              min: { value: 1, message: 'At least one location is required' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Number of locations"
                type="number"
                fullWidth
                size="small"
                margin="normal"
                error={!!errors.locations}
                helperText={errors.locations ? errors.locations.message : ''}
              />
            )}
          />
          <FormControl component="fieldset" margin="normal">
            <FormLabel component="legend">
              How would you like to be contacted?
            </FormLabel>
            <Controller
              name="contactPreference"
              control={control}
              defaultValue={[]} // now an array
              rules={{
                validate: (value) =>
                  value.length > 0 || 'At least one contact method is required',
              }}
              render={({ field }) => {
                const { value, onChange } = field;
                const handleCheckboxChange = (event: {
                  target: { checked: any; value: any };
                }) => {
                  const { checked, value: selectedValue } = event.target;
                  if (checked) {
                    onChange([...value, selectedValue]);
                  } else {
                    onChange(value.filter((val) => val !== selectedValue));
                  }
                };
                return (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value.includes('phone')}
                            onChange={handleCheckboxChange}
                            value="phone"
                          />
                        }
                        label="Phone"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value.includes('email')}
                            onChange={handleCheckboxChange}
                            value="email"
                          />
                        }
                        label="Email"
                      />
                    </Box>
                    {errors.contactPreference && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ marginTop: 1 }}
                      >
                        {errors.contactPreference.message}
                      </Typography>
                    )}
                  </>
                );
              }}
            />
          </FormControl>
          <Stack spacing={2} mt={2} mb={2}>
            <LoadingButton
              loading={isRequestEnterpriseInfoLoading}
              variant="contained"
              type="submit"
              color="primary"
            >
              Submit
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              onClick={handleClose}
              color="secondary"
              disabled={isRequestEnterpriseInfoLoading}
            >
              Cancel
            </LoadingButton>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReqMoreInfoModal;
